// src/pages/campus&comunity/clubs/ClubsTab.js
import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import {
  Grid,
  Paper,
  Button,
  IconButton,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  useMediaQuery,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  Box,
  Chip,
  TablePagination,
} from "@mui/material";
import {
  ArrowDropDown,
  Close,
  Edit,
  Delete,
  CheckCircle,
  Error,
  Upload,
  Warning,
  Visibility,
} from "@mui/icons-material";
import Search from "../../../components/common/search/Search";
import Input from "../../../components/common/input/Input";
import axiosInstance from "../../../utils/axios/axiosInstance";
import loaderGif from "../../../assets/images/loading.gif";

const ClubsTab = () => {
  // ─── State Hooks ────────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("");
  const [selectedDateSort, setSelectedDateSort] = useState("");
  const [clubData, setClubData] = useState([]);
  const [sortedData, setSortedData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [settings, setSettings] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [alert, setAlert] = useState({ type: "", message: "" });
  const [categories, setCategories] = useState([]);
  const [upcomingClubs, setUpcomingClubs] = useState([]);

  // Enhanced validation states following Events pattern
  const [validationErrors, setValidationErrors] = useState({});
  const [validationTouched, setValidationTouched] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);

  // Form fields
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [date, setDate] = useState("");
  const [location, setLocation] = useState("");
  const [imageFile, setImageFile] = useState(null);

  const [deleteDialog, setDeleteDialog] = useState({ open: false, club: null });
  const [viewDialog, setViewDialog] = useState({ open: false, club: null });
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const isLgScreen = useMediaQuery((theme) => theme.breakpoints.up("lg"));

  // Today's date for min attribute
  const todayString = new Date().toISOString().split("T")[0];

  // Enhanced validation functions following Events pattern
  const validateName = useCallback((name) => {
    const trimmedName = name?.trim() || '';
    
    if (!trimmedName) {
      return 'Club name is required';
    }
    if (trimmedName.length < 2) {
      return 'Club name must be at least 2 characters long';
    }
    if (trimmedName.length > 100) {
      return 'Club name must not exceed 100 characters';
    }
    if (!/^[a-zA-Z0-9\s\-\_\.\,\!\?\&\(\)]+$/.test(trimmedName)) {
      return 'Club name contains invalid characters';
    }
    return null;
  }, []);

  const validateCategory = useCallback((category) => {
    if (!category) {
      return 'Category is required';
    }
    
    const numericId = parseInt(category);
    if (isNaN(numericId) || numericId < 1) {
      return 'Please select a valid category';
    }
    
    if (categories.length > 0 && !categories.find(cat => cat.id.toString() === category.toString())) {
      return 'Selected category does not exist';
    }
    return null;
  }, [categories]);

  const validateDate = useCallback((date) => {
    if (!date) {
      return 'Date is required';
    }
    
    const selectedDate = new Date(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (selectedDate < today) {
      return 'Club date cannot be in the past';
    }
    
    const maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear() + 2);
    if (selectedDate > maxDate) {
      return 'Club date cannot be more than 2 years in the future';
    }
    
    return null;
  }, []);

  const validateDescription = useCallback((description) => {
    const trimmedDescription = description?.trim() || '';
    
    if (!trimmedDescription) {
      return 'Description is required';
    }
    if (trimmedDescription.length < 10) {
      return 'Description must be at least 10 characters long';
    }
    if (trimmedDescription.length > 1000) {
      return 'Description must not exceed 1000 characters';
    }
    return null;
  }, []);

  const validateLocation = useCallback((location) => {
    const trimmedLocation = location?.trim() || '';
    
    if (!trimmedLocation) {
      return 'Location is required';
    }
    if (trimmedLocation.length < 3) {
      return 'Location must be at least 3 characters long';
    }
    if (trimmedLocation.length > 200) {
      return 'Location must not exceed 200 characters';
    }
    return null;
  }, []);

  const validateEmail = useCallback((email) => {
    const trimmedEmail = email?.trim() || '';
    
    if (!trimmedEmail) {
      return null; // Email is optional
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      return 'Please enter a valid email address';
    }
    
    return null;
  }, []);

  const validatePhone = useCallback((phone) => {
    const trimmedPhone = phone?.trim() || '';
    
    if (!trimmedPhone) {
      return null; // Phone is optional
    }
    
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    if (!phoneRegex.test(trimmedPhone)) {
      return 'Please enter a valid phone number';
    }
    
    return null;
  }, []);

  const validateWebsite = useCallback((website) => {
    const trimmedWebsite = website?.trim() || '';
    
    if (!trimmedWebsite) {
      return null; // Website is optional
    }
    
    try {
      new URL(trimmedWebsite);
      return null;
    } catch {
      return 'Please enter a valid website URL';
    }
  }, []);

  const validateImage = useCallback((image) => {
    if (!image) {
      return 'Image is required';
    }
    
    if (typeof image === 'string') {
      return null;
    }
    
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(image.type)) {
      return 'Only JPEG, PNG, GIF, and WebP images are allowed';
    }
    
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (image.size > maxSize) {
      return 'Image size must not exceed 10MB';
    }
    
    const minSize = 1024; // 1KB minimum
    if (image.size < minSize) {
      return 'Image file appears to be corrupted or too small';
    }
    
    return null;
  }, []);

  // Form validation following Events pattern
  const validateForm = useCallback((formData = {
    name,
    category,
    date,
    description,
    location,
    email,
    phone,
    website,
    imageFile
  }) => {
    const errors = {};

    const nameError = validateName(formData.name);
    if (nameError) errors.name = nameError;

    const categoryError = validateCategory(formData.category);
    if (categoryError) errors.category = categoryError;

    const dateError = validateDate(formData.date);
    if (dateError) errors.date = dateError;

    const descriptionError = validateDescription(formData.description);
    if (descriptionError) errors.description = descriptionError;

    const locationError = validateLocation(formData.location);
    if (locationError) errors.location = locationError;

    const emailError = validateEmail(formData.email);
    if (emailError) errors.email = emailError;

    const phoneError = validatePhone(formData.phone);
    if (phoneError) errors.phone = phoneError;

    const websiteError = validateWebsite(formData.website);
    if (websiteError) errors.website = websiteError;

    const imageError = validateImage(formData.imageFile);
    if (imageError) errors.imageFile = imageError;

    setValidationErrors(errors);
    const isValid = Object.keys(errors).length === 0;
    setIsFormValid(isValid);

    return { isValid, errors };
  }, [
    name, category, date, description, location, email, phone, website, imageFile,
    validateName, validateCategory, validateDate, validateDescription, validateLocation,
    validateEmail, validatePhone, validateWebsite, validateImage
  ]);

  const validateField = useCallback((fieldName, value) => {
    let error = null;

    switch (fieldName) {
      case 'name':
        error = validateName(value);
        break;
      case 'category':
        error = validateCategory(value);
        break;
      case 'date':
        error = validateDate(value);
        break;
      case 'description':
        error = validateDescription(value);
        break;
      case 'location':
        error = validateLocation(value);
        break;
      case 'email':
        error = validateEmail(value);
        break;
      case 'phone':
        error = validatePhone(value);
        break;
      case 'website':
        error = validateWebsite(value);
        break;
      case 'imageFile':
        error = validateImage(value);
        break;
      default:
        break;
    }

    setValidationErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));

    return error;
  }, [
    validateName, validateCategory, validateDate, validateDescription, validateLocation,
    validateEmail, validatePhone, validateWebsite, validateImage
  ]);

  const markFieldAsTouched = useCallback((fieldName) => {
    setValidationTouched(prev => ({
      ...prev,
      [fieldName]: true
    }));
  }, []);

  const getFieldError = useCallback((fieldName) => {
    return validationTouched[fieldName] && validationErrors[fieldName];
  }, [validationTouched, validationErrors]);

  const isFieldValid = useCallback((fieldName) => {
    return validationTouched[fieldName] && !validationErrors[fieldName];
  }, [validationTouched, validationErrors]);

  const handleInputChange = useCallback((field, value) => {
    switch (field) {
      case 'name':
        setName(value);
        break;
      case 'category':
        setCategory(value);
        break;
      case 'date':
        setDate(value);
        break;
      case 'description':
        setDescription(value);
        break;
      case 'location':
        setLocation(value);
        break;
      case 'email':
        setEmail(value);
        break;
      case 'phone':
        setPhone(value);
        break;
      case 'website':
        setWebsite(value);
        break;
      case 'imageFile':
        setImageFile(value);
        break;
      default:
        break;
    }

    markFieldAsTouched(field);
    validateField(field, value);
  }, [markFieldAsTouched, validateField]);

  // Trigger validation when form values change
  useEffect(() => {
    if (Object.keys(validationTouched).length > 0) {
      validateForm();
    }
  }, [name, category, date, description, location, email, phone, website, imageFile, validateForm, validationTouched]);

  // ─── Effect Hooks ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (alert.message) {
      const timer = setTimeout(() => {
        setAlert({ type: "", message: "" });
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [alert.message]);

  useEffect(() => {
    fetchClubs();
    fetchCategories();
    fetchUpcomingClubs();
  }, []);

  useEffect(() => {
    setSortedData(clubData);
  }, [clubData]);

  // ─── API Calls ─────────────────────────────────────────────────────────────
  const fetchClubs = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(
        `${process.env.REACT_APP_COMMUNITY_API_BASE_URL}v1/clubs`
      );
      if (res?.data?.status === 200) {
        setClubData(res.data.data || []);
        setSortedData(res.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching clubs:", err);
      toast.error("Failed to load clubs");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axiosInstance.get(
        `${process.env.REACT_APP_COMMUNITY_API_BASE_URL}v1/clubs/categories`
      );
      if (res?.data?.status === 200) {
        setCategories(res.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  const fetchUpcomingClubs = async () => {
    try {
      const res = await axiosInstance.get(
        `${process.env.REACT_APP_COMMUNITY_API_BASE_URL}v1/clubs/upcoming`
      );
      if (res?.data?.status === 200) {
        setUpcomingClubs(res.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching upcoming clubs:", err);
      setUpcomingClubs([]);
    }
  };

  // Enhanced clear form function following Events pattern
  const clearFormAndErrors = useCallback(() => {
    setEditingId(null);
    setName("");
    setCategory("");
    setDescription("");
    setEmail("");
    setPhone("");
    setWebsite("");
    setDate(todayString);
    setLocation("");
    setImageFile(null);
    setValidationErrors({});
    setValidationTouched({});
    setIsFormValid(false);
  }, [todayString]);

  // Enhanced submit function following Events pattern
  const handleSubmit = useCallback(async () => {
    const requiredFields = ['name', 'category', 'date', 'description', 'location', 'imageFile'];
    requiredFields.forEach(field => markFieldAsTouched(field));

    const { isValid, errors } = validateForm();

    if (!isValid) {
      toast.error('Please fix the validation errors before submitting', {
        position: "top-center",
        autoClose: 5000,
      });
      return;
    }

    const formData = new FormData();
    formData.append("name", name.trim());
    formData.append("category", String(category));
    formData.append("description", description.trim());
    formData.append("email", email.trim());
    formData.append("phone", phone.trim());
    formData.append("website", website.trim());
    formData.append("date", date);
    formData.append("location", location.trim());
    if (imageFile) formData.append("image", imageFile);
    if (isEdit && editingId) formData.append("id", editingId);

    const url = isEdit
      ? `${process.env.REACT_APP_COMMUNITY_API_BASE_URL}v1/clubs/edit`
      : `${process.env.REACT_APP_COMMUNITY_API_BASE_URL}v1/clubs/create`;

    try {
      setLoading(true);
      const res = await axiosInstance.post(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const success = res.data.status === 200 || res.data.status === 201;

      if (success) {
        toast.success(`Club ${isEdit ? "updated" : "created"} successfully!`, {
          position: "top-center",
          autoClose: 3000,
        });

        await fetchClubs();
        handleCloseForm();
      } else {
        toast.error(res.data.message || "Something went wrong.", {
          position: "top-center",
          autoClose: 5000,
        });
      }
    } catch (err) {
      console.error("Error submitting club:", err);
      
      let errorMessage = `Failed to ${isEdit ? "update" : "create"} club`;
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      
      toast.error(errorMessage, {
        position: "top-center",
        autoClose: 5000,
      });
    } finally {
      setLoading(false);
    }
  }, [
    name, category, description, email, phone, website, date, location, imageFile,
    isEdit, editingId, markFieldAsTouched, validateForm
  ]);

  const handleDelete = async () => {
    const clubToDelete = deleteDialog.club;
    if (!clubToDelete) return;

    try {
      setLoading(true);
      const res = await axiosInstance.delete(
        `${process.env.REACT_APP_COMMUNITY_API_BASE_URL}v1/clubs/${clubToDelete.id}`
      );
      if (res?.data?.status === 200) {
        toast.success("Club deleted successfully.", {
          position: "top-center",
          autoClose: 3000,
        });
        await fetchClubs();
      } else {
        toast.error("Failed to delete club.", {
          position: "top-center",
          autoClose: 5000,
        });
      }
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("Delete failed. Try again.", {
        position: "top-center",
        autoClose: 5000,
      });
    } finally {
      setLoading(false);
      setDeleteDialog({ open: false, club: null });
    }
  };

  // ─── Event Handlers ──────────────────────────────────────────────────────────
  const openAddForm = () => {
    clearFormAndErrors();
    setIsEdit(false);
    setEditingId(null);
    setSettings(true);
  };

  const openEditForm = (club) => {
    setName(club.name || "");
    setCategory(club.category || "");
    setDescription(club.description || "");
    setEmail(club.email || "");
    setPhone(club.phone || "");
    setWebsite(club.website || "");
    setDate(club.date || "");
    setLocation(club.location || "");
    setImageFile(club.image || null);
    setEditingId(club.id);
    setIsEdit(true);
    setSettings(true);
    
    setValidationErrors({});
    setValidationTouched({});
    
    setTimeout(() => {
      const editFormData = {
        name: club.name,
        category: club.category,
        description: club.description,
        email: club.email,
        phone: club.phone,
        website: club.website,
        date: club.date,
        location: club.location,
        imageFile: club.image
      };
      validateForm(editFormData);
    }, 100);
  };

  const openViewDialog = (club) => {
    setViewDialog({ open: true, club: club });
  };

  const handleCloseForm = () => {
    clearFormAndErrors();
    setSettings(false);
    setEditingId(null);
    setIsEdit(false);
  };

  const handleSearch = (value) => {
    setSearchQuery(value);
    const filteredData = clubData?.filter((row) =>
      Object.values(row).some((val) =>
        String(val).toLowerCase().includes(value.toLowerCase())
      )
    ) || [];
    setSortedData(filteredData);
  };

  const handleSort = (filterValue) => {
    setSelectedFilter(filterValue);
    if (!filterValue) {
      setSortedData(clubData);
      return;
    }
    const filtered = clubData.filter(
      (item) => item.category === filterValue
    );
    setSortedData(filtered);
  };

  const handleDateSort = (sortValue) => {
    setSelectedDateSort(sortValue);
    
    const currentData = selectedFilter 
      ? clubData.filter((item) => item.category === selectedFilter)
      : clubData;
    
    if (!sortValue) {
      setSortedData(currentData);
      return;
    }
    
    const sortedArray = [...currentData];
    sortedArray.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      
      if (sortValue === "newest") {
        return dateB - dateA;
      } else if (sortValue === "oldest") {
        return dateA - dateB;
      }
      return 0;
    });
    
    setSortedData(sortedArray);
  };

  // Enhanced form content rendering following Events pattern
  const renderFormContent = () => (
    <>
      {/* Club Name */}
      <div className="form-group" style={{ marginBottom: '24px' }}>
        <label style={{
          display: 'block',
          marginBottom: '8px',
          fontWeight: 600,
          color: '#333',
          fontFamily: '"Saira", sans-serif'
        }}>
          Club Name <span style={{ color: '#ef4444' }}>*</span>
        </label>
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            placeholder="Enter club name"
            value={name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            onBlur={() => markFieldAsTouched('name')}
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: getFieldError('name') ? '2px solid #ef4444' : 
                     isFieldValid('name') ? '2px solid #10b981' : '1px solid #ddd',
              borderRadius: '8px',
              fontSize: '16px',
              fontFamily: '"Saira", sans-serif',
              backgroundColor: getFieldError('name') ? 'rgba(239, 68, 68, 0.05)' :
                              isFieldValid('name') ? 'rgba(16, 185, 129, 0.05)' : 'white',
              outline: 'none',
              boxSizing: 'border-box',
              paddingRight: (isFieldValid('name') || getFieldError('name')) ? '40px' : '16px'
            }}
            onFocus={(e) => {
              if (!getFieldError('name')) {
                e.target.style.borderColor = '#ce55b9';
              }
            }}
          />
          {isFieldValid('name') && (
            <CheckCircle style={{ 
              position: 'absolute', 
              right: '12px', 
              top: '50%', 
              transform: 'translateY(-50%)', 
              color: '#10b981', 
              fontSize: '20px' 
            }} />
          )}
          {getFieldError('name') && (
            <Error style={{ 
              position: 'absolute', 
              right: '12px', 
              top: '50%', 
              transform: 'translateY(-50%)', 
              color: '#ef4444', 
              fontSize: '20px' 
            }} />
          )}
        </div>
        <div style={{
          marginTop: '4px',
          fontSize: '12px',
          color: getFieldError('name') ? '#ef4444' : '#666',
          fontFamily: '"Saira", sans-serif'
        }}>
          {getFieldError('name') || 'Enter a descriptive name for your club'}
        </div>
      </div>

      {/* Category */}
      <div className="form-group" style={{ marginBottom: '24px' }}>
        <label style={{
          display: 'block',
          marginBottom: '8px',
          fontWeight: 600,
          color: '#333',
          fontFamily: '"Saira", sans-serif'
        }}>
          Category <span style={{ color: '#ef4444' }}>*</span>
        </label>
        <FormControl
          fullWidth
          className="!my-2"
          error={!!getFieldError('category')}
          style={{ position: 'relative' }}
        >
          <Select
            value={category}
            onChange={(e) => handleInputChange('category', e.target.value)}
            onBlur={() => markFieldAsTouched('category')}
            displayEmpty
            disabled={loading}
            IconComponent={(props) => (
              <ArrowDropDown
                {...props}
                style={{ color: "#ce55b9" }}
              />
            )}
            style={{
              borderRadius: '8px',
              fontFamily: '"Saira", sans-serif',
              fontSize: '16px',
              border: getFieldError('category') ? '2px solid #ef4444' : 
                     isFieldValid('category') ? '2px solid #10b981' : '1px solid #ddd',
              backgroundColor: getFieldError('category') ? 'rgba(239, 68, 68, 0.05)' :
                              isFieldValid('category') ? 'rgba(16, 185, 129, 0.05)' : 'white',
            }}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              }
            }}
          >
            <MenuItem value="" disabled style={{ fontFamily: '"Saira", sans-serif' }}>
              Select a category
            </MenuItem>
            {categories.length > 0 &&
              categories.map((e, i) => (
                <MenuItem value={e?.id} key={i} style={{ fontFamily: '"Saira", sans-serif' }}>
                  {e?.name}
                </MenuItem>
              ))}
          </Select>
          {isFieldValid('category') && (
            <CheckCircle style={{ 
              position: 'absolute', 
              right: '40px', 
              top: '50%', 
              transform: 'translateY(-50%)', 
              color: '#10b981', 
              fontSize: '20px',
              pointerEvents: 'none'
            }} />
          )}
          {getFieldError('category') && (
            <Error style={{ 
              position: 'absolute', 
              right: '40px', 
              top: '50%', 
              transform: 'translateY(-50%)', 
              color: '#ef4444', 
              fontSize: '20px',
              pointerEvents: 'none'
            }} />
          )}
        </FormControl>
        <div style={{
          marginTop: '4px',
          fontSize: '12px',
          color: getFieldError('category') ? '#ef4444' : '#666',
          fontFamily: '"Saira", sans-serif'
        }}>
          {getFieldError('category') || 'Choose the most appropriate category for your club'}
        </div>
      </div>

      {/* Date */}
      <div className="form-group" style={{ marginBottom: '24px' }}>
        <label style={{
          display: 'block',
          marginBottom: '8px',
          fontWeight: 600,
          color: '#333',
          fontFamily: '"Saira", sans-serif'
        }}>
          Date <span style={{ color: '#ef4444' }}>*</span>
        </label>
        <div style={{ position: 'relative' }}>
          <input
            type="date"
            value={date}
            min={todayString}
            onChange={(e) => handleInputChange('date', e.target.value)}
            onBlur={() => markFieldAsTouched('date')}
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: getFieldError('date') ? '2px solid #ef4444' : 
                     isFieldValid('date') ? '2px solid #10b981' : '1px solid #ddd',
              borderRadius: '8px',
              fontSize: '16px',
              fontFamily: '"Saira", sans-serif',
              outline: 'none',
              cursor: 'pointer',
              backgroundColor: getFieldError('date') ? 'rgba(239, 68, 68, 0.05)' :
                              isFieldValid('date') ? 'rgba(16, 185, 129, 0.05)' : 'white',
              boxSizing: 'border-box',
              paddingRight: (isFieldValid('date') || getFieldError('date')) ? '40px' : '16px'
            }}
            onFocus={(e) => {
              e.target.showPicker();
              if (!getFieldError('date')) {
                e.target.style.borderColor = '#ce55b9';
              }
            }}
          />
          {isFieldValid('date') && (
            <CheckCircle style={{ 
              position: 'absolute', 
              right: '32px', 
              top: '50%', 
              transform: 'translateY(-50%)', 
              color: '#10b981', 
              fontSize: '20px' 
            }} />
          )}
          {getFieldError('date') && (
            <Error style={{ 
              position: 'absolute', 
              right: '32px', 
              top: '50%', 
              transform: 'translateY(-50%)', 
              color: '#ef4444', 
              fontSize: '20px' 
            }} />
          )}
        </div>
        <div style={{
          marginTop: '4px',
          fontSize: '12px',
          color: getFieldError('date') ? '#ef4444' : '#666',
          fontFamily: '"Saira", sans-serif'
        }}>
          {getFieldError('date') || 'Select the club establishment/meeting date'}
        </div>
      </div>

      {/* Description */}
      <div className="form-group" style={{ marginBottom: '24px' }}>
        <label style={{
          display: 'block',
          marginBottom: '8px',
          fontWeight: 600,
          color: '#333',
          fontFamily: '"Saira", sans-serif'
        }}>
          Description <span style={{ color: '#ef4444' }}>*</span>
        </label>
        <div style={{ position: 'relative' }}>
          <textarea
            placeholder="Enter club description"
            rows={4}
            value={description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            onBlur={() => markFieldAsTouched('description')}
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: getFieldError('description') ? '2px solid #ef4444' : 
                     isFieldValid('description') ? '2px solid #10b981' : '1px solid #ddd',
              borderRadius: '8px',
              fontSize: '16px',
              fontFamily: '"Saira", sans-serif',
              outline: 'none',
              resize: 'vertical',
              minHeight: '100px',
              backgroundColor: getFieldError('description') ? 'rgba(239, 68, 68, 0.05)' :
                              isFieldValid('description') ? 'rgba(16, 185, 129, 0.05)' : 'white',
              boxSizing: 'border-box',
              paddingRight: (isFieldValid('description') || getFieldError('description')) ? '40px' : '16px'
            }}
            onFocus={(e) => {
              if (!getFieldError('description')) {
                e.target.style.borderColor = '#ce55b9';
              }
            }}
          />
          {isFieldValid('description') && (
            <CheckCircle style={{ 
              position: 'absolute', 
              right: '12px', 
              top: '12px', 
              color: '#10b981', 
              fontSize: '20px' 
            }} />
          )}
          {getFieldError('description') && (
            <Error style={{ 
              position: 'absolute', 
              right: '12px', 
              top: '12px', 
              color: '#ef4444', 
              fontSize: '20px' 
            }} />
          )}
        </div>
        <div style={{
          marginTop: '4px',
          fontSize: '12px',
          color: getFieldError('description') ? '#ef4444' : '#666',
          fontFamily: '"Saira", sans-serif'
        }}>
          {getFieldError('description') || 'Provide details about your club (10-1000 characters)'}
        </div>
      </div>

      {/* Location */}
      <div className="form-group" style={{ marginBottom: '24px' }}>
        <label style={{
          display: 'block',
          marginBottom: '8px',
          fontWeight: 600,
          color: '#333',
          fontFamily: '"Saira", sans-serif'
        }}>
          Location <span style={{ color: '#ef4444' }}>*</span>
        </label>
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            placeholder="Enter club location"
            value={location}
            onChange={(e) => handleInputChange('location', e.target.value)}
            onBlur={() => markFieldAsTouched('location')}
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: getFieldError('location') ? '2px solid #ef4444' : 
                     isFieldValid('location') ? '2px solid #10b981' : '1px solid #ddd',
              borderRadius: '8px',
              fontSize: '16px',
              fontFamily: '"Saira", sans-serif',
              outline: 'none',
              backgroundColor: getFieldError('location') ? 'rgba(239, 68, 68, 0.05)' :
                              isFieldValid('location') ? 'rgba(16, 185, 129, 0.05)' : 'white',
              boxSizing: 'border-box',
              paddingRight: (isFieldValid('location') || getFieldError('location')) ? '40px' : '16px'
            }}
            onFocus={(e) => {
              if (!getFieldError('location')) {
                e.target.style.borderColor = '#ce55b9';
              }
            }}
          />
          {isFieldValid('location') && (
            <CheckCircle style={{ 
              position: 'absolute', 
              right: '12px', 
              top: '50%', 
              transform: 'translateY(-50%)', 
              color: '#10b981', 
              fontSize: '20px' 
            }} />
          )}
          {getFieldError('location') && (
            <Error style={{ 
              position: 'absolute', 
              right: '12px', 
              top: '50%', 
              transform: 'translateY(-50%)', 
              color: '#ef4444', 
              fontSize: '20px' 
            }} />
          )}
        </div>
        <div style={{
          marginTop: '4px',
          fontSize: '12px',
          color: getFieldError('location') ? '#ef4444' : '#666',
          fontFamily: '"Saira", sans-serif'
        }}>
          {getFieldError('location') || 'Specify where the club meets'}
        </div>
      </div>

      {/* Email */}
      <div className="form-group" style={{ marginBottom: '24px' }}>
        <label style={{
          display: 'block',
          marginBottom: '8px',
          fontWeight: 600,
          color: '#333',
          fontFamily: '"Saira", sans-serif'
        }}>
          Email
        </label>
        <div style={{ position: 'relative' }}>
          <input
            type="email"
            placeholder="Enter contact email"
            value={email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            onBlur={() => markFieldAsTouched('email')}
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: getFieldError('email') ? '2px solid #ef4444' : 
                     isFieldValid('email') ? '2px solid #10b981' : '1px solid #ddd',
              borderRadius: '8px',
              fontSize: '16px',
              fontFamily: '"Saira", sans-serif',
              outline: 'none',
              backgroundColor: getFieldError('email') ? 'rgba(239, 68, 68, 0.05)' :
                              isFieldValid('email') ? 'rgba(16, 185, 129, 0.05)' : 'white',
              boxSizing: 'border-box',
              paddingRight: (isFieldValid('email') || getFieldError('email')) ? '40px' : '16px'
            }}
            onFocus={(e) => {
              if (!getFieldError('email')) {
                e.target.style.borderColor = '#ce55b9';
              }
            }}
          />
          {isFieldValid('email') && (
            <CheckCircle style={{ 
              position: 'absolute', 
              right: '12px', 
              top: '50%', 
              transform: 'translateY(-50%)', 
              color: '#10b981', 
              fontSize: '20px' 
            }} />
          )}
          {getFieldError('email') && (
            <Error style={{ 
              position: 'absolute', 
              right: '12px', 
              top: '50%', 
              transform: 'translateY(-50%)', 
              color: '#ef4444', 
              fontSize: '20px' 
            }} />
          )}
        </div>
        <div style={{
          marginTop: '4px',
          fontSize: '12px',
          color: getFieldError('email') ? '#ef4444' : '#666',
          fontFamily: '"Saira", sans-serif'
        }}>
          {getFieldError('email') || 'Contact email for the club (optional)'}
        </div>
      </div>

      {/* Phone */}
      <div className="form-group" style={{ marginBottom: '24px' }}>
        <label style={{
          display: 'block',
          marginBottom: '8px',
          fontWeight: 600,
          color: '#333',
          fontFamily: '"Saira", sans-serif'
        }}>
          Phone
        </label>
        <div style={{ position: 'relative' }}>
          <input
            type="tel"
            placeholder="Enter contact phone"
            value={phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            onBlur={() => markFieldAsTouched('phone')}
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: getFieldError('phone') ? '2px solid #ef4444' : 
                     isFieldValid('phone') ? '2px solid #10b981' : '1px solid #ddd',
              borderRadius: '8px',
              fontSize: '16px',
              fontFamily: '"Saira", sans-serif',
              outline: 'none',
              backgroundColor: getFieldError('phone') ? 'rgba(239, 68, 68, 0.05)' :
                              isFieldValid('phone') ? 'rgba(16, 185, 129, 0.05)' : 'white',
              boxSizing: 'border-box',
              paddingRight: (isFieldValid('phone') || getFieldError('phone')) ? '40px' : '16px'
            }}
            onFocus={(e) => {
              if (!getFieldError('phone')) {
                e.target.style.borderColor = '#ce55b9';
              }
            }}
          />
          {isFieldValid('phone') && (
            <CheckCircle style={{ 
              position: 'absolute', 
              right: '12px', 
              top: '50%', 
              transform: 'translateY(-50%)', 
              color: '#10b981', 
              fontSize: '20px' 
            }} />
          )}
          {getFieldError('phone') && (
            <Error style={{ 
              position: 'absolute', 
              right: '12px', 
              top: '50%', 
              transform: 'translateY(-50%)', 
              color: '#ef4444', 
              fontSize: '20px' 
            }} />
          )}
        </div>
        <div style={{
          marginTop: '4px',
          fontSize: '12px',
          color: getFieldError('phone') ? '#ef4444' : '#666',
          fontFamily: '"Saira", sans-serif'
        }}>
          {getFieldError('phone') || 'Contact phone number for the club (optional)'}
        </div>
      </div>

      {/* Website */}
      <div className="form-group" style={{ marginBottom: '24px' }}>
        <label style={{
          display: 'block',
          marginBottom: '8px',
          fontWeight: 600,
          color: '#333',
          fontFamily: '"Saira", sans-serif'
        }}>
          Website
        </label>
        <div style={{ position: 'relative' }}>
          <input
            type="url"
            placeholder="Enter club website"
            value={website}
            onChange={(e) => handleInputChange('website', e.target.value)}
            onBlur={() => markFieldAsTouched('website')}
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: getFieldError('website') ? '2px solid #ef4444' : 
                     isFieldValid('website') ? '2px solid #10b981' : '1px solid #ddd',
              borderRadius: '8px',
              fontSize: '16px',
              fontFamily: '"Saira", sans-serif',
              outline: 'none',
              backgroundColor: getFieldError('website') ? 'rgba(239, 68, 68, 0.05)' :
                              isFieldValid('website') ? 'rgba(16, 185, 129, 0.05)' : 'white',
              boxSizing: 'border-box',
              paddingRight: (isFieldValid('website') || getFieldError('website')) ? '40px' : '16px'
            }}
            onFocus={(e) => {
              if (!getFieldError('website')) {
                e.target.style.borderColor = '#ce55b9';
              }
            }}
          />
          {isFieldValid('website') && (
            <CheckCircle style={{ 
              position: 'absolute', 
              right: '12px', 
              top: '50%', 
              transform: 'translateY(-50%)', 
              color: '#10b981', 
              fontSize: '20px' 
            }} />
          )}
          {getFieldError('website') && (
            <Error style={{ 
              position: 'absolute', 
              right: '12px', 
              top: '50%', 
              transform: 'translateY(-50%)', 
              color: '#ef4444', 
              fontSize: '20px' 
            }} />
          )}
        </div>
        <div style={{
          marginTop: '4px',
          fontSize: '12px',
          color: getFieldError('website') ? '#ef4444' : '#666',
          fontFamily: '"Saira", sans-serif'
        }}>
          {getFieldError('website') || 'Club website URL (optional)'}
        </div>
      </div>

      {/* Image */}
      <div className="form-group" style={{ marginBottom: '24px' }}>
        <label style={{
          display: 'block',
          marginBottom: '8px',
          fontWeight: 600,
          color: '#333',
          fontFamily: '"Saira", sans-serif'
        }}>
          Club Image <span style={{ color: '#ef4444' }}>*</span>
        </label>
        <div style={{ position: 'relative' }}>
          <div
            style={{
              border: getFieldError('imageFile') ? '2px dashed #ef4444' : 
                     isFieldValid('imageFile') ? '2px dashed #10b981' : '2px dashed #ddd',
              borderRadius: '8px',
              padding: '24px',
              textAlign: 'center',
              backgroundColor: getFieldError('imageFile') ? 'rgba(239, 68, 68, 0.05)' :
                              isFieldValid('imageFile') ? 'rgba(16, 185, 129, 0.05)' : '#fafafa',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease'
            }}
            onClick={() => !loading && document.getElementById('club-image-input').click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              if (!loading) {
                const files = e.dataTransfer.files;
                if (files.length > 0) {
                  handleInputChange('imageFile', files[0]);
                }
              }
            }}
          >
            <input
              id="club-image-input"
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  handleInputChange('imageFile', file);
                }
              }}
              onBlur={() => markFieldAsTouched('imageFile')}
              disabled={loading}
              style={{ display: 'none' }}
            />
            
            {isEdit && imageFile && typeof imageFile === 'string' ? (
              <div>
                <img 
                  src={imageFile} 
                  alt="Current club image" 
                  style={{
                    maxWidth: '200px',
                    maxHeight: '150px',
                    borderRadius: '8px',
                    marginBottom: '12px',
                    objectFit: 'cover'
                  }}
                />
                <Typography style={{ fontFamily: '"Saira", sans-serif', fontSize: '14px', color: '#374151', marginBottom: '4px' }}>
                  Current Image - Click to change
                </Typography>
              </div>
            ) : imageFile && typeof imageFile !== 'string' ? (
              <div>
                <Upload style={{ fontSize: '48px', color: '#10b981', marginBottom: '8px' }} />
                <Typography style={{ fontFamily: '"Saira", sans-serif', fontSize: '14px', color: '#374151', marginBottom: '4px' }}>
                  New image selected: {imageFile.name}
                </Typography>
              </div>
            ) : (
              <div>
                <Upload style={{ fontSize: '48px', color: '#9ca3af', marginBottom: '8px' }} />
                <Typography style={{ fontFamily: '"Saira", sans-serif', fontSize: '14px', color: '#374151', marginBottom: '4px' }}>
                  Click to upload or drag & drop
                </Typography>
              </div>
            )}
            
            <Typography style={{ fontFamily: '"Saira", sans-serif', fontSize: '12px', color: '#6b7280' }}>
              PNG, JPG, GIF, WebP up to 10MB
            </Typography>
            {isFieldValid('imageFile') && (
              <CheckCircle style={{ 
                position: 'absolute', 
                right: '12px', 
                top: '12px', 
                color: '#10b981', 
                fontSize: '24px' 
              }} />
            )}
            {getFieldError('imageFile') && (
              <Error style={{ 
                position: 'absolute', 
                right: '12px', 
                top: '12px', 
                color: '#ef4444', 
                fontSize: '24px' 
              }} />
            )}
          </div>
        </div>
        <div style={{
          marginTop: '4px',
          fontSize: '12px',
          color: getFieldError('imageFile') ? '#ef4444' : '#666',
          fontFamily: '"Saira", sans-serif'
        }}>
          {getFieldError('imageFile') || 'Upload an attractive image for your club'}
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Top-right Alert */}
      {alert.message && (
        <Alert
          severity={alert.type}
          sx={{
            position: "fixed",
            top: 16,
            right: 16,
            zIndex: 1400,
            minWidth: 240,
          }}
        >
          {alert.message}
        </Alert>
      )}

      {/* Main Page Wrapper */}
      <Grid container spacing={2}>
        <div className="container">
          <div className="css-11ezll3-MuiToolbar-root breadcrumbs">
            <div className="top-sec">
              <div className="card custom-card">
                <div className="card-body">
                  {/* Header */}
                  <div className="d-flex align-items-center justify-content-between flex-wrap pb-1">
                    <div className="d-flex align-items-center">
                      <h3 className="mb-2">My Clubs List</h3>
                    </div>
                    <div className="d-flex align-items-center flex-wrap mb-1">
                      <Button 
                        variant="contained" 
                        color="primary" 
                        className="!w-fit !px-3 !h-10"
                        onClick={openAddForm}
                      >
                        + Add New Club
                      </Button>
                    </div>
                  </div>

                  {/* Search + Filter */}
                  <div className="row filter-panel mt-4">
                    <div className="col-md-4">
                      <div className="serarch-bar">
                        <Search
                          value={searchQuery}
                          handleInputChange={(e) => handleSearch(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="col-md-4"></div>
                    <div className="col-md-2">
                      <div className="w-full flex items-center justify-end gap-2">
                        <FormControl fullWidth className="filter">
                          <InputLabel style={{ lineHeight: "14px" }}>
                            Sort by Date
                          </InputLabel>
                          <Select
                            value={selectedDateSort}
                            onChange={(e) => handleDateSort(e.target.value)}
                            label="Sort by Date"
                            IconComponent={(props) => (
                              <ArrowDropDown
                                {...props}
                                style={{ color: "#c942b1" }}
                              />
                            )}
                            style={{ height: 40 }}
                          >
                            <MenuItem value="">None</MenuItem>
                            <MenuItem value="newest">Newest First</MenuItem>
                            <MenuItem value="oldest">Oldest First</MenuItem>
                          </Select>
                        </FormControl>
                      </div>
                    </div>
                    <div className="col-md-2">
                      <div className="w-full flex items-center justify-end gap-2">
                        <FormControl fullWidth className="filter">
                          <InputLabel style={{ lineHeight: "14px" }}>
                            Filter
                          </InputLabel>
                          <Select
                            value={selectedFilter}
                            onChange={(e) => handleSort(e.target.value)}
                            label="Filter"
                            IconComponent={(props) => (
                              <ArrowDropDown {...props} style={{ color: "#c942b1" }} />
                            )}
                            style={{ height: 40 }}
                          >
                            <MenuItem value="">None</MenuItem>
                            {categories.length > 0 &&
                              categories.map((e, i) => (
                                <MenuItem value={e?.id} key={i}>
                                  {e?.name}
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      </div>
                    </div>
                  </div>

                  {/* Table */}
                  <div className="row">
                    <div className="col-md-12">
                      <div className="table-responsive mb-0">
                        {loading ? (
                          <div className="w-full flex justify-center items-center py-10">
                            <img src={loaderGif} alt="Loading..." className="loading-icon-img" />
                          </div>
                        ) : (
                          <TableContainer className="table table-hover table-bordered mb-0 text-md-nowrap text-lg-nowrap text-xl-nowrap table-striped">
                            <Table>
                              <TableHead>
                                <TableRow>
                                  <TableCell>S.No</TableCell>
                                  <TableCell>Image</TableCell>
                                  <TableCell>Club Name</TableCell>
                                  <TableCell>Category</TableCell>
                                  <TableCell>Date</TableCell>
                                  <TableCell>Location</TableCell>
                                  <TableCell>Description</TableCell>
                                  <TableCell align="center">Action</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {sortedData.length > 0 ? (
                                  sortedData
                                    .slice(
                                      currentPage * rowsPerPage,
                                      currentPage * rowsPerPage + rowsPerPage
                                    )
                                    .map((club, idx) => (
                                      <TableRow key={club.id}>
                                        <TableCell>{currentPage * rowsPerPage + idx + 1}</TableCell>
                                        <TableCell>
                                          {club.image ? (
                                            <img
                                              src={club.image}
                                              alt={club.name}
                                              className="w-[100px] h-[70px] rounded-md"
                                            />
                                          ) : (
                                            <Typography variant="body2">
                                              No Image
                                            </Typography>
                                          )}
                                        </TableCell>
                                        <TableCell>{club.name}</TableCell>
                                        <TableCell>
                                          {categories.find((cat) => cat.id.toString() === club.category.toString())?.name || "N/A"}
                                        </TableCell>
                                        <TableCell>{club.date}</TableCell>
                                        <TableCell>{club.location || "N/A"}</TableCell>
                                        <TableCell>{club.description || "N/A"}</TableCell>
                                        <TableCell align="center">
                                          <div className="flex items-center gap-1 active-btn">
                                            <div 
                                              className="btn btn-success btn-icon"
                                              onClick={() => openViewDialog(club)}
                                              title="View Club Details"
                                            >
                                              <Visibility color="primary" fontSize="small" />
                                            </div>
                                            <div 
                                              className="btn btn-indigo btn-icon"
                                              onClick={() => openEditForm(club)}
                                              title="Edit Club"
                                            >
                                              <Edit color="primary" fontSize="small" />
                                            </div>
                                            <div 
                                              className="btn btn-denger btn-icon"
                                              onClick={() => setDeleteDialog({ open: true, club: club })}
                                              title="Delete Club"
                                            >
                                              <Delete color="primary" fontSize="small" />
                                            </div>
                                          </div>
                                        </TableCell>
                                      </TableRow>
                                    ))
                                ) : (
                                  <TableRow>
                                    <TableCell colSpan={8} align="center">
                                      <p className="p-4 text-center text-gray-400">
                                        No clubs found
                                      </p>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                            <TablePagination
                              component="div"
                              count={sortedData.length}
                              page={currentPage}
                              onPageChange={(e, newPage) => setCurrentPage(newPage)}
                              rowsPerPage={rowsPerPage}
                              onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setCurrentPage(0);
                              }}
                              rowsPerPageOptions={[5, 10, 25, 50]}
                            />
                          </TableContainer>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Upcoming Clubs Section */}
        <div className="container">
          <div className="row">
            <div className="col-md-12">
              <div className="css-11ezll3-MuiToolbar-root breadcrumbs">
                <div className="top-sec">
                  <div className="card custom-card">
                    <div className="card-body">
                      <div className="d-flex align-items-center justify-content-between flex-wrap pb-1">
                        <div className="d-flex align-items-center">
                          <h3 className="mt-2">Upcoming Clubs List</h3>
                        </div>
                        {upcomingClubs.length > 0 && (
                          <div className="text-sm text-gray-500">
                            {upcomingClubs.length} upcoming club(s) found
                          </div>
                        )}
                      </div>

                      {loading ? (
                        <div className="w-full flex justify-center items-center py-10">
                          <img src={loaderGif} alt="Loading..." className="loading-icon-img" />
                          <span className="ml-2">Loading upcoming clubs...</span>
                        </div>
                      ) : upcomingClubs.length > 0 ? (
                        upcomingClubs.map((club, idx) => (
                          <div key={club.id || idx} className="col-md-12">
                            <div className="upcoming-event-list">
                              <div className="row">
                                <div className="col-md-2">
                                  <div className="image-show">
                                    <img
                                      src={club.image}
                                      alt={club.name}
                                      onError={(e) => {
                                        e.target.src = "/default-club-image.png";
                                      }}
                                    />
                                  </div>
                                </div>
                                <div className="col-md-10">
                                  <div className="content-area">
                                    <ul>
                                      <li>
                                        <svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" className="h-6 w-6">
                                          <defs>
                                            <style type="text/css">{`
                                              .cls-1 { fill: #fff; }
                                              .cls-2 { fill: #1394f6; }
                                            `}</style>
                                          </defs>
                                          <title />
                                          <g data-name="filled outline" id="filled_outline">
                                            <rect
                                              className="cls-1"
                                              height="50"
                                              rx="5"
                                              transform="translate(66.5 2.5) rotate(90)"
                                              width="41"
                                              x="11.5"
                                              y="9.5"
                                            />
                                            <path
                                              className="cls-2"
                                              d="M7,24V19a5,5,0,0,1,5-5H52a5,5,0,0,1,5,5v5Z"
                                            />
                                            <path
                                              className="cls-1"
                                              d="M20,19a2,2,0,0,1-2-2V11a2,2,0,0,1,4,0v6A2,2,0,0,1,20,19Z"
                                            />
                                            <path
                                              className="cls-1"
                                              d="M44,19a2,2,0,0,1-2-2V11a2,2,0,0,1,4,0v6A2,2,0,0,1,44,19Z"
                                            />
                                            <path
                                              d="M52,13H47V11a3,3,0,0,0-6,0v2H23V11a3,3,0,0,0-6,0v2H12a6,6,0,0,0-6,6V50a6,6,0,0,0,6,6H52a6,6,0,0,0,6-6V19A6,6,0,0,0,52,13Zm-9-2a1,1,0,0,1,2,0v6a1,1,0,0,1-2,0ZM19,11a1,1,0,0,1,2,0v6a1,1,0,0,1-2,0Zm-7,4h5v2a3,3,0,0,0,6,0V15H41v2a3,3,0,0,0,6,0V15h5a4,4,0,0,1,4,4v5H8V19A4,4,0,0,1,12,15ZM52,54H12a4,4,0,0,1-4-4V26H56V50A4,4,0,0,1,52,54ZM20,32v4H16V32h4m2-2H14v8h8V30ZM20,44v4H16V44h4m2-2H14v8h8V42Zm28-7H26V33H50Zm0,12H26V45H50Z"
                                              fill="#000"
                                            />
                                          </g>
                                        </svg>
                                        {club.date}
                                      </li>
                                      <li><strong>Category:</strong> {categories.find((cat) => cat.id.toString() === club.category.toString())?.name || "N/A"}</li>
                                      <li><strong>Location:</strong> {club.location}</li>
                                      <li><strong>Email:</strong> {club.email || 'Not available'}</li>
                                      <li><strong>Phone:</strong> {club.phone || 'Not available'}</li>
                                      <li><strong>Website:</strong> {club.website || 'Not available'}</li>
                                    </ul>

                                    <h2>{club.name}</h2>
                                    <p>{club.description}</p>
                                    
                                    <Button
                                      variant="contained"
                                      style={{
                                        background: 'linear-gradient(135deg, #ce55b9 0%, #b8479a 100%)',
                                        color: 'white',
                                        fontFamily: '"Saira", sans-serif',
                                        textTransform: 'none',
                                        borderRadius: '8px',
                                        padding: '8px 16px'
                                      }}
                                    >
                                      Join Club
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8">
                          <p className="text-gray-500 text-lg">
                            No upcoming clubs found
                          </p>
                          <p className="text-gray-400 text-sm mt-2">
                            Check back later for new clubs from other users
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Grid>

      {/* Form Modal - Updated to match Event Form UI Design */}
      {settings && (
        <div style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '450px',
          maxWidth: '90vw',
          backgroundColor: 'white',
          boxShadow: '-4px 0 20px rgba(0,0,0,0.1)',
          zIndex: 1300,
          display: 'flex',
          flexDirection: 'column',
          fontFamily: '"Saira", sans-serif'
        }}>
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div style={{
              background: '#ce55b9',
              color: 'white',
              padding: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <h5 style={{ 
                  fontSize: '20px', 
                  fontWeight: 600, 
                  fontFamily: '"Saira", sans-serif',
                  margin: 0
                }}>
                  {isEdit ? 'Edit Club' : 'Add New Club'}
                </h5>
              </div>
              <button
                onClick={handleCloseForm}
                disabled={loading}
                style={{
                  color: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  border: 'none',
                  borderRadius: '6px',
                  width: '32px',
                  height: '32px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: loading ? 0.6 : 1
                }}
              >
                ✕
              </button>
            </div>

            {/* Content */}
            <div style={{
              flex: 1,
              padding: '24px',
              overflow: 'auto',
              background: '#ffffff'
            }}>
              {renderFormContent()}
            </div>

            {/* Footer Actions */}
            <div style={{
              padding: '24px',
              borderTop: '1px solid rgba(0,0,0,0.1)',
              background: 'white',
              display: 'flex',
              gap: '16px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={handleCloseForm}
                disabled={loading}
                style={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontFamily: '"Saira", sans-serif',
                  padding: '12px 24px',
                  minWidth: '100px',
                  backgroundColor: 'transparent',
                  color: '#666',
                  border: '1px solid #ddd',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  opacity: loading ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!isFormValid || loading}
                style={{
                  background: '#ce55b9',
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontFamily: '"Saira", sans-serif',
                  padding: '12px 24px',
                  minWidth: '140px',
                  color: 'white',
                  border: 'none',
                  cursor: (!isFormValid || loading) ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  opacity: (!isFormValid || loading) ? 0.6 : 1
                }}
              >
                {loading ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid transparent',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    <span style={{ fontFamily: '"Saira", sans-serif' }}>
                      {isEdit ? 'Updating...' : 'Creating...'}
                    </span>
                  </>
                ) : (
                  <span style={{ fontFamily: '"Saira", sans-serif' }}>
                    {isEdit ? 'Update Club' : 'Add Club'}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Overlay */}
      {settings && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1200
          }}
          onClick={handleCloseForm}
        />
      )}

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      {/* View Club Details Dialog */}
      <Dialog
        open={viewDialog.open}
        onClose={() => setViewDialog({ open: false, club: null })}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '8px',
            padding: '0px'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          p: 3,
          fontFamily: '"Saira", sans-serif',
          fontSize: '20px',
          fontWeight: 600,
          backgroundColor: '#f8f9fa',
          borderBottom: '1px solid #e9ecef'
        }}>
          <Visibility sx={{ color: '#6c757d', fontSize: 24 }} />
          Club Details
          <Box sx={{ flex: 1 }} />
          <Box
            onClick={() => setViewDialog({ open: false, club: null })}
            sx={{
              p: 1,
              borderRadius: '4px',
              backgroundColor: '#e9ecef',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: '#dee2e6'
              }
            }}
          >
            <Close sx={{ fontSize: 18, color: '#6c757d' }} />
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          {viewDialog.club && (
            <Box>
              {/* Club Image */}
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                {viewDialog.club.image ? (
                  <img
                    src={viewDialog.club.image}
                    alt={viewDialog.club.name}
                    style={{
                      maxWidth: '300px',
                      maxHeight: '200px',
                      borderRadius: '4px',
                      border: '1px solid #dee2e6'
                    }}
                  />
                ) : (
                  <Box sx={{
                    width: '300px',
                    height: '200px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px',
                    border: '1px solid #dee2e6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#6c757d',
                    margin: '0 auto'
                  }}>
                    No Image Available
                  </Box>
                )}
              </Box>

              {/* Club Information */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                {/* Club Name */}
                <Box>
                  <Typography sx={{ 
                    fontFamily: '"Saira", sans-serif',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#495057',
                    mb: 1
                  }}>
                    Club Name
                  </Typography>
                  <Typography sx={{ 
                    fontFamily: '"Saira", sans-serif',
                    fontSize: '16px',
                    color: '#212529',
                    padding: '8px 12px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px',
                    border: '1px solid #dee2e6'
                  }}>
                    {viewDialog.club.name}
                  </Typography>
                </Box>

                {/* Category */}
                <Box>
                  <Typography sx={{ 
                    fontFamily: '"Saira", sans-serif',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#495057',
                    mb: 1
                  }}>
                    Category
                  </Typography>
                  <Typography sx={{ 
                    fontFamily: '"Saira", sans-serif',
                    fontSize: '16px',
                    color: '#212529',
                    padding: '8px 12px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px',
                    border: '1px solid #dee2e6'
                  }}>
                    {categories.find((cat) => cat.id.toString() === viewDialog.club.category.toString())?.name || "N/A"}
                  </Typography>
                </Box>

                {/* Date */}
                <Box>
                  <Typography sx={{ 
                    fontFamily: '"Saira", sans-serif',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#495057',
                    mb: 1
                  }}>
                    Date
                  </Typography>
                  <Typography sx={{ 
                    fontFamily: '"Saira", sans-serif',
                    fontSize: '16px',
                    color: '#212529',
                    padding: '8px 12px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px',
                    border: '1px solid #dee2e6'
                  }}>
                    {viewDialog.club.date}
                  </Typography>
                </Box>

                {/* Location */}
                <Box>
                  <Typography sx={{ 
                    fontFamily: '"Saira", sans-serif',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#495057',
                    mb: 1
                  }}>
                    Location
                  </Typography>
                  <Typography sx={{ 
                    fontFamily: '"Saira", sans-serif',
                    fontSize: '16px',
                    color: '#212529',
                    padding: '8px 12px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px',
                    border: '1px solid #dee2e6'
                  }}>
                    {viewDialog.club.location || "N/A"}
                  </Typography>
                </Box>

                {/* Email */}
                <Box>
                  <Typography sx={{ 
                    fontFamily: '"Saira", sans-serif',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#495057',
                    mb: 1
                  }}>
                    Email
                  </Typography>
                  <Typography sx={{ 
                    fontFamily: '"Saira", sans-serif',
                    fontSize: '16px',
                    color: '#212529',
                    padding: '8px 12px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px',
                    border: '1px solid #dee2e6'
                  }}>
                    {viewDialog.club.email || "Not provided"}
                  </Typography>
                </Box>

                {/* Phone */}
                <Box>
                  <Typography sx={{ 
                    fontFamily: '"Saira", sans-serif',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#495057',
                    mb: 1
                  }}>
                    Phone
                  </Typography>
                  <Typography sx={{ 
                    fontFamily: '"Saira", sans-serif',
                    fontSize: '16px',
                    color: '#212529',
                    padding: '8px 12px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px',
                    border: '1px solid #dee2e6'
                  }}>
                    {viewDialog.club.phone || "Not provided"}
                  </Typography>
                </Box>
              </Box>

              {/* Website - Full Width */}
              {viewDialog.club.website && (
                <Box sx={{ mt: 2 }}>
                  <Typography sx={{ 
                    fontFamily: '"Saira", sans-serif',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#495057',
                    mb: 1
                  }}>
                    Website
                  </Typography>
                  <Typography sx={{ 
                    fontFamily: '"Saira", sans-serif',
                    fontSize: '16px',
                    color: '#212529',
                    padding: '8px 12px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px',
                    border: '1px solid #dee2e6'
                  }}>
                    <a 
                      href={viewDialog.club.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ 
                        color: '#0d6efd', 
                        textDecoration: 'none'
                      }}
                    >
                      {viewDialog.club.website}
                    </a>
                  </Typography>
                </Box>
              )}

              {/* Description - Full Width */}
              <Box sx={{ mt: 2 }}>
                <Typography sx={{ 
                  fontFamily: '"Saira", sans-serif',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#495057',
                  mb: 1
                }}>
                  Description
                </Typography>
                <Typography sx={{ 
                  fontFamily: '"Saira", sans-serif',
                  fontSize: '16px',
                  color: '#212529',
                  padding: '12px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '4px',
                  border: '1px solid #dee2e6',
                  lineHeight: 1.5,
                  minHeight: '80px'
                }}>
                  {viewDialog.club.description || "No description provided"}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, club: null })}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '12px',
            padding: '8px'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          pb: 1,
          fontFamily: '"Saira", sans-serif',
          fontSize: '20px',
          fontWeight: 600
        }}>
          <Warning sx={{ color: '#f59e0b', fontSize: 28 }} />
          Confirm Delete Club
        </DialogTitle>
        
        <DialogContent sx={{ pt: 1 }}>
          <DialogContentText sx={{ 
            fontFamily: '"Saira", sans-serif',
            fontSize: '16px',
            color: '#374151',
            mb: 2
          }}>
            Are you sure you want to delete the club <strong>"{deleteDialog.club?.name}"</strong>?
          </DialogContentText>
          
          <Box sx={{
            backgroundColor: '#fef3c7',
            border: '1px solid #f59e0b',
            borderRadius: '8px',
            p: 2,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1
          }}>
            <Warning sx={{ color: '#f59e0b', fontSize: 20, mt: 0.2 }} />
            <Box>
              <Typography sx={{ 
                fontFamily: '"Saira", sans-serif',
                fontSize: '14px',
                fontWeight: 600,
                color: '#92400e',
                mb: 0.5
              }}>
                Warning: This action cannot be undone
              </Typography>
              <Typography sx={{ 
                fontFamily: '"Saira", sans-serif',
                fontSize: '13px',
                color: '#92400e'
              }}>
                All club data and related information will be permanently removed.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ 
          p: 3, 
          pt: 2,
          gap: 2,
          justifyContent: 'flex-end'
        }}>
          <Button
            onClick={() => setDeleteDialog({ open: false, club: null })}
            variant="outlined"
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontFamily: '"Saira", sans-serif',
              px: 3,
              py: 1,
              borderColor: '#e5e7eb',
              color: '#6b7280',
              '&:hover': {
                borderColor: '#d1d5db',
                backgroundColor: '#f9fafb'
              }
            }}
          >
            Cancel
          </Button>
          
          <Button
            onClick={handleDelete}
            variant="contained"
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontFamily: '"Saira", sans-serif',
              px: 3,
              py: 1,
              backgroundColor: '#dc2626',
              '&:hover': {
                backgroundColor: '#b91c1c'
              }
            }}
            startIcon={<Delete />}
          >
            Delete Club
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ClubsTab;